import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { AdminColors } from '../../../constants/adminColors';
import FontFamily from '../../../../../constants/fonts';
import { SelectOption } from '../../../components/AdminSelect';
import { ALL_COLUMNS } from './ColumnPickerModal';

interface DataLabelPickerModalProps {
  visible: boolean;
  value: string;
  options: SelectOption[];
  onClose: () => void;
  onApply: (value: string) => void;
}

const ChevronIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <Svg width="13" height="13" viewBox="0 0 24 24" fill="none">
    <Path
      d={open ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'}
      stroke={AdminColors.blue900}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const RadioIcon: React.FC<{ selected: boolean }> = ({ selected }) => (
  <View style={[pickerStyles.radioOuter, selected && pickerStyles.radioOuterActive]}>
    {selected && <View style={pickerStyles.radioInner} />}
  </View>
);

const DataLabelSection: React.FC<{
  title: string;
  options: SelectOption[];
  selectedValue: string;
  onSelect: (value: string) => void;
}> = ({ title, options, selectedValue, onSelect }) => {
  const [expanded, setExpanded] = useState(true);

  if (options.length === 0) {
    return null;
  }

  return (
    <View style={sectionStyles.container}>
      <TouchableOpacity
        style={sectionStyles.toggle}
        onPress={() => setExpanded(prev => !prev)}
        activeOpacity={0.7}
      >
        <View style={sectionStyles.toggleTitleRow}>
          <ChevronIcon open={expanded} />
          <Text style={sectionStyles.toggleLabel}>{title}</Text>
        </View>
      </TouchableOpacity>

      {expanded && (
        <View style={sectionStyles.body}>
          {options.map(option => {
            const selected = option.value === selectedValue;

            return (
              <TouchableOpacity
                key={option.value}
                style={[sectionStyles.item, selected && sectionStyles.itemSelected]}
                onPress={() => onSelect(option.value)}
                activeOpacity={0.7}
              >
                <RadioIcon selected={selected} />
                <Text style={sectionStyles.itemLabel} numberOfLines={1}>
                  {option.label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      )}
    </View>
  );
};

const DataLabelPickerModal: React.FC<DataLabelPickerModalProps> = ({
  visible,
  value,
  options,
  onClose,
  onApply,
}) => {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    if (visible) {
      setDraft(value);
    }
  }, [visible, value]);

  const sections = useMemo(() => {
    const sectionMap = new Map<string, SelectOption[]>();

    options
      .filter(option => option.value !== 'none')
      .forEach(option => {
        const column = ALL_COLUMNS.find(col => col.key === option.value);
        const section = column?.section ?? 'Other';
        const existing = sectionMap.get(section) ?? [];
        existing.push(option);
        sectionMap.set(section, existing);
      });

    const orderedSections = Array.from(sectionMap.entries()).map(([section, sectionOptions]) => ({
      section,
      options: sectionOptions,
    }));

    const noneOption = options.find(option => option.value === 'none');
    if (noneOption) {
      orderedSections.unshift({
        section: 'Display options',
        options: [noneOption],
      });
    }

    return orderedSections;
  }, [options]);

  const selectedLabel = options.find(option => option.value === draft)?.label ?? 'None';

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <TouchableOpacity
        style={pickerStyles.backdrop}
        activeOpacity={1}
        onPress={onClose}
      />
      <View style={pickerStyles.container}>
        <View style={pickerStyles.header}>
          <View>
            <Text style={pickerStyles.title}>Map data labels</Text>
            <Text style={pickerStyles.subtitle}>
              Select one field to display beside every mapped location.
            </Text>
          </View>
          <TouchableOpacity onPress={onClose} style={pickerStyles.closeBtn} activeOpacity={0.7}>
            <Text style={pickerStyles.closeTxt}>✕</Text>
          </TouchableOpacity>
        </View>

        <View style={pickerStyles.selectedRow}>
          <Text style={pickerStyles.selectedLabel}>Current selection</Text>
          <Text style={pickerStyles.selectedValue}>{selectedLabel}</Text>
        </View>

        <ScrollView style={pickerStyles.scroll} showsVerticalScrollIndicator={false}>
          {sections.map(section => (
            <DataLabelSection
              key={section.section}
              title={section.section}
              options={section.options}
              selectedValue={draft}
              onSelect={setDraft}
            />
          ))}

          <View style={{ height: 8 }} />
        </ScrollView>

        <View style={pickerStyles.footer}>
          <TouchableOpacity onPress={onClose} style={pickerStyles.cancelBtn} activeOpacity={0.7}>
            <Text style={pickerStyles.cancelTxt}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => {
              onApply(draft);
              onClose();
            }}
            style={pickerStyles.applyBtn}
            activeOpacity={0.7}
          >
            <Text style={pickerStyles.applyTxt}>Apply</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const sectionStyles = StyleSheet.create({
  container: {
    marginBottom: 7,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 7,
    overflow: 'hidden',
  },
  toggle: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 34,
    backgroundColor: AdminColors.blue25,
    paddingHorizontal: 9,
  },
  toggleTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  toggleLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: AdminColors.blue900,
    fontFamily: FontFamily.bold,
  },
  body: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    padding: 6,
    gap: 2,
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    paddingVertical: 6,
    paddingHorizontal: 6,
    borderRadius: 4,
    minWidth: '48%',
    flex: 0,
  },
  itemSelected: {
    backgroundColor: '#EAF1FB',
  },
  itemLabel: {
    fontSize: 11,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
    flex: 1,
  },
});

const pickerStyles = StyleSheet.create({
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(18,35,55,0.2)',
  },
  container: {
    position: 'absolute',
    right: 28,
    top: 160,
    width: 440,
    maxHeight: '72%' as any,
    backgroundColor: AdminColors.bgSurface,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 8,
    shadowColor: '#002E69',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.18,
    shadowRadius: 24,
    elevation: 12,
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.gray25,
  },
  title: {
    fontSize: 13,
    fontWeight: '700',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.bold,
  },
  subtitle: {
    fontSize: 10,
    color: AdminColors.textMuted,
    fontFamily: FontFamily.regular,
    marginTop: 1,
    maxWidth: 320,
  },
  closeBtn: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: AdminColors.gray25,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  closeTxt: {
    fontSize: 12,
    color: AdminColors.textSecondary,
  },
  selectedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.gray25,
    backgroundColor: '#FAFCFF',
  },
  selectedLabel: {
    fontSize: 10,
    color: AdminColors.textMuted,
    fontFamily: FontFamily.regular,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  selectedValue: {
    fontSize: 11,
    color: AdminColors.blue900,
    fontFamily: FontFamily.bold,
  },
  scroll: {
    flex: 1,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
    padding: 12,
    borderTopWidth: 1,
    borderTopColor: AdminColors.gray25,
  },
  cancelBtn: {
    height: 32,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: AdminColors.blue800,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cancelTxt: {
    fontSize: 12,
    fontWeight: '700',
    color: AdminColors.blue800,
    fontFamily: FontFamily.bold,
  },
  applyBtn: {
    height: 32,
    paddingHorizontal: 20,
    backgroundColor: AdminColors.blue800,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  applyTxt: {
    fontSize: 12,
    fontWeight: '700',
    color: '#FFFFFF',
    fontFamily: FontFamily.bold,
  },
  radioOuter: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  radioOuterActive: {
    borderColor: AdminColors.blue700,
  },
  radioInner: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: AdminColors.blue700,
  },
});

export default DataLabelPickerModal;